import React, { useState, useEffect } from 'react';
import {
    Button, Table, Card, Row, Col, Form, InputGroup,
    Spinner, Modal, Badge, Pagination
} from 'react-bootstrap';
import {
    collection, query, where, orderBy, getDocs, doc,
    deleteDoc, updateDoc, serverTimestamp, getFirestore
} from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import HabilidadForm from './HabilidadForm';

const HabilidadesScreen = ({ usuario }) => {
    // Estados para gestionar la pantalla
    const [habilidades, setHabilidades] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentHabilidad, setCurrentHabilidad] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Acceder a Firestore
    const db = getFirestore(firebaseApp);

    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';

    // Cargar habilidades al iniciar
    useEffect(() => {
        loadHabilidades();
    }, [ruc]);

    // Función para cargar habilidades de la empresa
    const loadHabilidades = async () => {
        try {
            setLoading(true);
            const habilidadesRef = collection(db, 'rs_habilidades');
            const q = query(
                habilidadesRef,
                where('ruc', '==', ruc),
                orderBy('nombre')
            );

            const snapshot = await getDocs(q);

            const habilidadesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setHabilidades(habilidadesList);
        } catch (error) {
            console.error("Error cargando habilidades:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar las habilidades. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar habilidades según término de búsqueda
    const filteredHabilidades = habilidades.filter(habilidad =>
        habilidad.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (habilidad.categoria && habilidad.categoria.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calcular paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentHabilidades = filteredHabilidades.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredHabilidades.length / itemsPerPage);

    // Cambiar de página
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Abrir modal para editar
    const handleEdit = (habilidad) => {
        setCurrentHabilidad(habilidad);
        setShowModal(true);
    };

    // Abrir modal para crear nueva habilidad
    const handleNew = () => {
        setCurrentHabilidad(null);
        setShowModal(true);
    };

    // Eliminar una habilidad
    const handleDelete = async (habilidadId, nombreHabilidad) => {
        try {
            const result = await Swal.fire({
                title: '¿Está seguro?',
                text: `Va a eliminar la habilidad "${nombreHabilidad}". Esta acción no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await deleteDoc(doc(db, 'rs_habilidades', habilidadId));

                Swal.fire(
                    'Eliminada',
                    'La habilidad ha sido eliminada correctamente.',
                    'success'
                );

                // Actualizar lista local sin necesidad de recargar de Firestore
                setHabilidades(prevHabilidades =>
                    prevHabilidades.filter(habilidad => habilidad.id !== habilidadId)
                );
            }
        } catch (error) {
            console.error("Error eliminando habilidad:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar la habilidad. Es posible que esté siendo utilizada por vacantes activas.'
            });
        }
    };

    // Guardar una habilidad (nueva o edición)
    const handleSaveHabilidad = async (formData) => {
        try {
            if (currentHabilidad) {
                // Actualizar habilidad existente
                await updateDoc(doc(db, 'rs_habilidades', currentHabilidad.id), {
                    ...formData,
                    fechaActualizacion: serverTimestamp()
                });

                // Actualizar lista local
                setHabilidades(prevHabilidades =>
                    prevHabilidades.map(habilidad =>
                        habilidad.id === currentHabilidad.id
                            ? { ...habilidad, ...formData }
                            : habilidad
                    )
                );

                Swal.fire('Actualizada', 'Habilidad actualizada correctamente', 'success');
            } else {
                // La creación se maneja en el componente HabilidadForm
                loadHabilidades(); // Recargar para obtener la habilidad recién creada
                Swal.fire('Creada', 'Habilidad creada correctamente', 'success');
            }

            setShowModal(false);
        } catch (error) {
            console.error("Error guardando habilidad:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al guardar la habilidad.'
            });
        }
    };

    return (
        <div className="p-1">
            <Row className="mb-4 align-items-center">
                <Col md={8}>
                    <h4 className="mb-0">Gestión de Habilidades y Competencias</h4>
                    <p className="text-muted mb-0">
                        Administre las habilidades y competencias para los procesos de selección
                    </p>
                </Col>
                <Col md={4} className="text-end">
                    <Button
                        variant="primary"
                        onClick={handleNew}
                        className="d-flex align-items-center ms-auto"
                    >
                        <FaPlus className="me-2" /> Nueva Habilidad
                    </Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nombre o categoría..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1); // Resetear a primera página
                                    }}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6} className="text-end text-muted">
                            {filteredHabilidades.length} habilidades encontradas
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Cargando habilidades...</p>
                        </div>
                    ) : currentHabilidades.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="mb-0 text-muted">
                                {searchTerm
                                    ? 'No se encontraron habilidades que coincidan con la búsqueda.'
                                    : 'No hay habilidades registradas.'}
                            </p>
                            {searchTerm && (
                                <Button
                                    variant="link"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Limpiar búsqueda
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Nombre</th>
                                        <th>Categoría</th>
                                        <th>Usos</th>
                                        <th>Fecha de Creación</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentHabilidades.map((habilidad, index) => (
                                        <tr key={habilidad.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>{habilidad.nombre}</td>
                                            <td>
                                                {habilidad.categoria ? (
                                                    <Badge bg="info" className="text-white">
                                                        {habilidad.categoria}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <Badge bg={habilidad.usos > 0 ? "success" : "secondary"}>
                                                    {habilidad.usos || 0}
                                                </Badge>
                                            </td>
                                            <td>
                                                {habilidad.fechaCreacion ?
                                                    new Date(habilidad.fechaCreacion.seconds * 1000).toLocaleDateString() :
                                                    'N/A'}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => handleEdit(habilidad)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleDelete(habilidad.id, habilidad.nombre)}
                                                        disabled={habilidad.usos > 0}
                                                        title={habilidad.usos > 0 ? "No se puede eliminar una habilidad en uso" : ""}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />

                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPage}
                                                onClick={() => paginate(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}

                                        <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para crear/editar habilidades */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                backdrop="static"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentHabilidad ? 'Editar Habilidad' : 'Nueva Habilidad'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <HabilidadForm
                        habilidad={currentHabilidad}
                        onSave={handleSaveHabilidad}
                        onCancel={() => setShowModal(false)}
                        usuario={usuario}
                    />
                </Modal.Body>
            </Modal>

            {/* Indicación para usuarios */}
            <Card className="bg-light">
                <Card.Body>
                    <h5>Información</h5>
                    <ul className="mb-0">
                        <li>Las habilidades pueden agruparse por categorías para facilitar su organización</li>
                        <li>Las habilidades en uso (con valor mayor a 0) no podrán ser eliminadas</li>
                        <li>Puede administrar estas habilidades al crear o editar vacantes</li>
                    </ul>
                </Card.Body>
            </Card>
        </div>
    );
};

export default HabilidadesScreen;

